import { Navigation, Pagination } from 'swiper';
import { MarketplaceListing } from '@/reports/types/vehicles';
import { Text, Carousel, Box, Link, useTheme } from '@/theme/index';
import { capitalizeString } from '@/utils/capitalizeString';
import { commaFormat, commaFormatCurrency } from '@/utils/index';
import { CarouselCard, ImagePill, ImageBox } from './components';

interface MarketplaceCarouselProps {
  marketplaceListings: MarketplaceListing[];
  isLoading: boolean;
  isError: boolean;
}

export function MarketplaceCarousel({
  marketplaceListings,
  isLoading,
  isError,
}: MarketplaceCarouselProps) {
  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error...</div>;

  const theme = useTheme();
  const { sm, md, lg } = theme.breakpoints.values;
  const config = {
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    modules: [Pagination, Navigation],
    spaceBetween: 20,
    className: 'default-carousel',
    watchSlidesProgress: true,
    breakpoints: {
      [sm]: { slidesPerView: '1' },
      [md]: { slidesPerView: '2' },
      [lg]: { slidesPerView: '3' },
    },
  };

  const listings: MarketplaceListing[] | [] = marketplaceListings || [];
  const carouselItems = listings
    .filter((listing: MarketplaceListing) => listing.vehicle?.images.length > 0)
    .map((listing: MarketplaceListing) => {
      const { vehicle, inventory_type } = listing;
      const image = vehicle?.images[0]?.url || vehicle?.images[1]?.url;
      if (!image) return null;
      const condition = inventory_type
        ? capitalizeString(inventory_type.toLowerCase())
        : null;

      return (
        <CarouselCard>
          <ImageBox src={image}>
            {condition && <ImagePill label={condition} />}
          </ImageBox>
          <Box padding={2}>
            <Text fontWeight="bold" marginBottom={1}>
              {vehicle.make} {vehicle.model} {vehicle.year}
            </Text>
            <Text variant="h4" marginBottom={0}>
              {commaFormatCurrency(vehicle?.price) || '-'}
            </Text>
            <Text variant="body2" sx={{ opacity: 0.6 }}>
              {commaFormat(vehicle?.mileage?.miles) || '-'} miles
            </Text>
            <Box
              width="100%"
              display="flex"
              justifyContent="center"
              sx={(theme) => ({
                marginTop: theme.spacing(2.5),
              })}
            >
              <Link
                target="_blank"
                rel="noopener noreferrer"
                href={`https://www.bumper.com/marketplace/listing/${listing.id}`}
                color="success.main"
              >
                View this vehicle
              </Link>
            </Box>
          </Box>
        </CarouselCard>
      );
    });

  return <Carousel elements={carouselItems} config={config} />;
}
